import { useEffect, useState } from 'react';
import { fetchBlobUrl, previewText } from '../../api/files.ts';
import { previewKindOf, formatBytes } from '../../lib/format.ts';
import { IconChevronLeft, IconChevronRight, IconClose, IconDownload } from '../../lib/icons.tsx';
import type { ApiNode } from '../../api/types.ts';

interface Props {
  namespaceId: string;
  node: ApiNode;
  siblings: ApiNode[];
  onNavigate: (node: ApiNode) => void;
  onDownload: (node: ApiNode) => void;
  onClose: () => void;
}

export function PreviewModal({
  namespaceId, node, siblings, onNavigate, onDownload, onClose,
}: Props) {
  const kind = previewKindOf(node.name, node.mime);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const previewable = siblings.filter(
    (s) => s.type === 'file' && previewKindOf(s.name, s.mime) !== 'none',
  );
  const index = previewable.findIndex((s) => s.id === node.id);

  useEffect(() => {
    let revoked: string | null = null;
    setBlobUrl(null); setText(null); setError(null);

    if (kind === 'text') {
      previewText(namespaceId, node.path)
        .then((r) => { setText(r.content + (r.truncated ? '\n\n… (이후 생략)' : '')); })
        .catch((e: Error) => { setError(e.message); });
    } else if (kind !== 'none') {
      fetchBlobUrl(namespaceId, node.path)
        .then((url) => { revoked = url; setBlobUrl(url); })
        .catch((e: Error) => { setError(e.message); });
    }

    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [namespaceId, node.path, kind, node.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onNavigate(previewable[index - 1]!);
      if (e.key === 'ArrowRight' && index >= 0 && index < previewable.length - 1) {
        onNavigate(previewable[index + 1]!);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [index, previewable, onNavigate, onClose]);

  return (
    <div className="preview-backdrop" role="dialog" aria-modal="true" aria-label={node.name}>
      <div className="preview__bar">
        <span className="truncate spacer">{node.name}</span>
        <span style={{ fontSize: 12, opacity: .7 }}>{formatBytes(node.size)}</span>
        <button className="btn" onClick={() => { onDownload(node); }}>
          <IconDownload size={16} /> 다운로드
        </button>
        <button className="btn btn--icon" aria-label="닫기" onClick={onClose}>
          <IconClose size={16} />
        </button>
      </div>

      <div className="preview__stage" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {error && <p style={{ color: '#fff' }}>{error}</p>}
        {!error && kind === 'image' && blobUrl && <img src={blobUrl} alt={node.name} />}
        {!error && kind === 'video' && blobUrl && <video src={blobUrl} controls autoPlay />}
        {!error && kind === 'audio' && blobUrl && <audio src={blobUrl} controls autoPlay />}
        {/* SVG/HTML 은 previewKindOf 에서 이미 제외된다. 여기 오는 건 브라우저 내장
            뷰어가 처리하는 PDF 뿐이라 sandbox 없이 렌더링한다 (빈 sandbox 는 뷰어를 막는다). */}
        {!error && kind === 'pdf' && blobUrl && (
          <iframe src={blobUrl} title={node.name} referrerPolicy="no-referrer" />
        )}
        {!error && kind === 'text' && (
          text === null ? <span className="spinner" /> : <pre className="preview__text">{text}</pre>
        )}
        {!error && kind === 'none' && (
          <p style={{ color: '#fff' }}>이 형식은 미리보기를 지원하지 않습니다. 다운로드해 주세요.</p>
        )}
        {!error && kind !== 'none' && kind !== 'text' && !blobUrl && <span className="spinner" />}
      </div>

      {index > 0 && (
        <button
          className="preview__nav preview__nav--prev" aria-label="이전"
          onClick={() => { onNavigate(previewable[index - 1]!); }}
        >
          <IconChevronLeft size={20} />
        </button>
      )}
      {index >= 0 && index < previewable.length - 1 && (
        <button
          className="preview__nav preview__nav--next" aria-label="다음"
          onClick={() => { onNavigate(previewable[index + 1]!); }}
        >
          <IconChevronRight size={20} />
        </button>
      )}
    </div>
  );
}
